import moment from "jalali-moment";

const PERSIAN_DIGITS = "۰۱۲۳۴۵۶۷۸۹";
const ARABIC_DIGITS = "٠١٢٣٤٥٦٧٨٩";

function toEnglishDigits(value: string): string {
  return value
    .replace(/[۰-۹]/g, (digit) => String(PERSIAN_DIGITS.indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String(ARABIC_DIGITS.indexOf(digit)));
}

/**
 * Formats an ISO jalali date string (`YYYY-MM-DD`) to a readable label
 * like `۷ اردیبهشت ۱۴۰۵`. Non ISO values are returned unchanged.
 */
export function formatJalaliDate(value: string | null | undefined): string {
  if (!value) return "";
  const normalized = toEnglishDigits(value.trim());
  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    return moment(normalized, "jYYYY-jMM-jDD")
      .locale("fa")
      .format("jD jMMMM jYYYY");
  }
  return value;
}

/**
 * Formats a gregorian ISO datetime to a numeric jalali date like `1403/05/20`.
 */
export function formatJalaliNumericDate(
  isoDate: string | null | undefined,
): string {
  if (!isoDate) return "";
  return moment(isoDate).locale("fa").format("jYYYY/jMM/jDD");
}

/**
 * Extracts the time portion (`HH:mm`) of a gregorian ISO datetime.
 */
export function formatIsoTime(isoDate: string | null | undefined): string {
  if (!isoDate) return "";
  return moment(isoDate).format("HH:mm");
}

export interface JalaliDateParts {
  year: number;
  month: number;
  day: number;
}

/**
 * Combines a jalali calendar selection with a `HH:mm` time into a gregorian
 * ISO datetime, which is the format the API accepts for `start_at`.
 */
export function jalaliPartsToIso(
  parts: JalaliDateParts,
  time: string = "00:00",
): string {
  const [hours = "00", minutes = "00"] = toEnglishDigits(time).split(":");

  return moment(
    `${parts.year}/${parts.month}/${parts.day} ${hours}:${minutes}`,
    "jYYYY/jM/jD HH:mm",
  ).toISOString();
}

/**
 * Converts jalali calendar parts into a gregorian `YYYY-MM-DD` date string,
 * the format the API accepts for `from_date`/`to_date`.
 */
export function jalaliPartsToDate(parts: JalaliDateParts): string {
  return moment(
    `${parts.year}/${parts.month}/${parts.day}`,
    "jYYYY/jM/jD",
  ).format("YYYY-MM-DD");
}

/**
 * Splits a gregorian ISO datetime into the jalali parts the calendar expects.
 */
export function isoToJalaliParts(
  isoDate: string | null | undefined,
): JalaliDateParts | null {
  if (!isoDate) return null;

  const date = moment(isoDate);
  if (!date.isValid()) return null;

  return {
    year: Number(date.format("jYYYY")),
    month: Number(date.format("jM")),
    day: Number(date.format("jD")),
  };
}

/**
 * Returns the gregorian date range covering a jalali month of the current
 * jalali year, e.g. month "1" -> `2026-03-21` to `2026-04-20`.
 */
export const getJalaliMonthRange = (
  month: string,
): { fromDate: string; toDate: string } => {
  const monthNumber = Number(month);
  const year = Number(moment().locale("fa").format("jYYYY"));

  const [nextYear, nextMonth] =
    monthNumber === 12 ? [year + 1, 1] : [year, monthNumber + 1];

  const firstDay = moment(`${year}/${monthNumber}/1`, "jYYYY/jM/jD");
  const lastDay = moment(`${nextYear}/${nextMonth}/1`, "jYYYY/jM/jD").subtract(
    1,
    "day",
  );

  return {
    fromDate: firstDay.format("YYYY-MM-DD"),
    toDate: lastDay.format("YYYY-MM-DD"),
  };
};
